import React, {ReactNode} from 'react';
import dayjs from 'dayjs';
import {BalloonEventModel} from 'ts-core';
import {ImageSection} from './ImageSection';
import {HeadSection} from './HeadSection';
import {LocationSection} from './LocationSection';
import {TimeSection} from './TimeSection';
import {DescriptionSection} from './DescriptionSection';
import {ContactSection} from './ContactSection';
import {SourcesSection} from './SourcesSection';
import {EventActionBar, type EventAction} from '../EventActions/EventActionBar';
import {CategoryCtaButton} from '../EventActions/CategoryCtaButton';
import {useTranslations} from '../../contexts/Translations';

export interface EventDetailsPanelProps {
    event: BalloonEventModel;
    /** Which built-in toolbar buttons to render. Default: save / calendar / share. */
    actions?: EventAction[];
    /** Extra toolbar nodes appended after the built-in actions (e.g. Edit, Clone on map sidebar). */
    extraActions?: ReactNode;
    /**
     * Render the embedded micro-map inside LocationSection (with the "Open full map"
     * preview overlay). Default `false` — e.g. the map sidebar already sits over
     * the real map, so a second micro-map would be redundant.
     */
    showMicroMap?: boolean;
    /** Render a prominent "Explore nearby on the map" pill below HeadSection. */
    showExploreCta?: boolean;
    /**
     * Optional replacement for the built-in micro-map inside LocationSection
     * (rendered only when `showMicroMap` is set). The site injects the full
     * balloon mini-map here; keeps react-ui decoupled from the `map` package.
     */
    locationMapSlot?: ReactNode;
    trackingSource?: string;
    /** Rendered above ImageSection (e.g. "Back to map" link on site). */
    headerSlot?: ReactNode;
    /** Rendered after SourcesSection/footer (e.g. mobile close button on map sidebar). */
    footerSlot?: ReactNode;
    className?: string;
    /** Desktop image height in rem. Default 15 (~240px). */
    imageHeight?: number;
    /** Mobile image height in rem. Default 12.5 (~200px). */
    imageMobileHeight?: number;
}

/**
 * Shared event details composition used by:
 *  - `site/src/pages/EventPage.tsx` — SEO landing page (with micro-map + explore CTA)
 *  - `map/src/app/pages/EventDetailPage.tsx` — sidebar / bottom sheet over the real map
 *
 * The caller supplies the outer shell (SEO meta tags, sidebar layout, bottom-sheet
 * positioning). This component renders the canonical inner content.
 */
export function EventDetailsPanel({
                                      event,
                                      actions = ['save', 'calendar', 'share'],
                                      extraActions,
                                      showMicroMap = false,
                                      showExploreCta = false,
                                      locationMapSlot,
                                      trackingSource,
                                      headerSlot,
                                      footerSlot,
                                      className,
                                      imageHeight = 15,
                                      imageMobileHeight = 12.5,
                                  }: EventDetailsPanelProps) {
    const {translated} = useTranslations();
    return (
        <div className={`event-details-panel ${className ?? ''}`}>
            {headerSlot}

            {/* Image is always flush to the container edges (no horizontal padding,
                no top rounding). Callers should NOT wrap this component in their
                own horizontal padding — the padding for content below is applied
                internally so the image stays edge-to-edge. */}
            <ImageSection
                event={event}
                slideHeight={imageHeight}
                mobileSlideHeight={imageMobileHeight}
            />

            <div className="px-4 md:px-8">
                {/* Unified toolbar: flat row with a single divider underneath.
                    OpenOnMap pill on the left when the site needs it, action
                    icons on the right. */}
                <div className="sticky top-0 z-40 mt-0 mb-6 py-4 flex items-center justify-between gap-3 px-0 w-full bg-white border-b border-gray-300">
                    {showExploreCta && event.category && event.category.slug !== 'other' ? (
                        <CategoryCtaButton
                            event={event}
                            category={event.category}
                            trackingSource={trackingSource}
                        />
                    ) : (
                        <>
                        </>
                    )}
                    <div className="flex items-center gap-2">
                        <EventActionBar
                            event={event}
                            actions={actions}
                            trackingSource={trackingSource}
                        />
                        {extraActions}
                    </div>
                </div>

                <div className="[&_.balloon-section]:border-gray-300 [&_.balloon-section]:border-solid [&_.balloon-section]:border-b-[1px] [&_.balloon-section]:pb-4 [&_.balloon-section]:mb-4">
                    <HeadSection event={event}/>

                    <LocationSection
                        event={event}
                        showMap={showMicroMap}
                        variant="preview"
                        trackingSource={trackingSource}
                        locationMapSlot={locationMapSlot}
                    />
                    <TimeSection event={event}/>
                    <DescriptionSection event={event}/>
                    <ContactSection event={event}/>
                    <SourcesSection event={event}/>
                </div>

                <footer className="flex flex-col w-full text-gray-400 mt-4 border-gray-300 pt-2 text-sm">
                    <div className="font-medium w-full">
                        {translated('last_modified')}: {dayjs(event.lastModified).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                </footer>

                {footerSlot}
            </div>
        </div>
    );
}
